import React, { useState } from 'react';
import { pricingBrigadeiros, pricingBombons, pricingBrigadeirosFesta } from '../data/mockData';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Check, ShoppingCart as CartIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import FlavorSelector from './FlavorSelector';
import ShoppingCartDisplay from './ShoppingCart';
import { useCart } from '../context/CartContext';

const Pricing = () => {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [currentBox, setCurrentBox] = useState(null);
  const [currentType, setCurrentType] = useState('');
  const { addToCart } = useCart();

  const openFlavorSelector = (item, type) => {
    setCurrentBox(item);
    setCurrentType(type);
    setSelectorOpen(true);
  };

  const PricingCard = ({ item, index, type }) => (
    <Card 
      key={index}
      className="border-2 border-amber-100 hover:border-amber-400 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
    >
      <CardHeader className="bg-gradient-to-br from-amber-50 to-orange-50 pb-4">
        <CardTitle className="text-center">
          <div className="text-4xl font-bold text-amber-900 mb-2">
            {item.units}
          </div>
          <div className="text-sm text-amber-700">unidades</div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="text-center mb-4">
          <div className="text-3xl font-bold text-amber-950">
            {item.price}
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 text-sm text-amber-700 mb-4">
          <Check className="w-4 h-4" />
          <span>{item.flavors} sabores</span>
        </div>
        <Button 
          className="w-full bg-amber-600 hover:bg-amber-700 text-white gap-2"
          onClick={() => openFlavorSelector(item, type)}
        >
          <CartIcon className="w-4 h-4" />
          Selecionar Sabores
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3">
            {/* Section header */}
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-amber-100 text-amber-800 hover:bg-amber-200">
                Preçário
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-amber-950 mb-4">
                Escolha Sua Caixa
              </h2>
              <p className="text-xl text-amber-700 max-w-2xl mx-auto">
                Clique em uma caixa e escolha seus sabores favoritos
              </p>
            </div>

            <Tabs defaultValue="brigadeiros20" className="w-full">
              <TabsList className="grid w-full max-w-4xl mx-auto grid-cols-1 md:grid-cols-3 h-auto gap-2 bg-amber-100 p-2 rounded-2xl mb-10">
                <TabsTrigger
                  value="brigadeiros20"
                  className="rounded-xl px-4 py-3 text-sm md:text-base data-[state=active]:bg-amber-700 data-[state=active]:text-white"
                >
                  Brigadeiros 20g
                </TabsTrigger>
                <TabsTrigger
                  value="brigadeirosFesta"
                  className="rounded-xl px-4 py-3 text-sm md:text-base data-[state=active]:bg-amber-700 data-[state=active]:text-white"
                >
                  Brigadeiros Festa 15g
                </TabsTrigger>
                <TabsTrigger
                  value="bombons"
                  className="rounded-xl px-4 py-3 text-sm md:text-base data-[state=active]:bg-amber-700 data-[state=active]:text-white"
                >
                  Bombons de Chocolate Belga
                </TabsTrigger>
              </TabsList>

              <TabsContent value="brigadeiros20" className="mt-0">
                <div className="mb-16">
                  <h3 className="text-2xl font-bold text-amber-900 mb-6 text-center">
                    Brigadeiros 20g
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                    {pricingBrigadeiros.map((item, index) => (
                      <PricingCard key={index} item={item} index={index} type="brigadeiros" />
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="brigadeirosFesta" className="mt-0">
                <div className="mb-16">
                  <h3 className="text-2xl font-bold text-amber-900 mb-6 text-center">
                    Brigadeiros Festa 15g
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto gap-6">
                    {pricingBrigadeirosFesta.map((item, index) => (
                      <Card
                        key={index}
                        className="border-2 border-amber-100 hover:border-amber-400 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                      >
                        <CardHeader className="bg-gradient-to-br from-amber-50 to-orange-50 pb-4">
                          <CardTitle className="text-center">
                            <div className="text-4xl font-bold text-amber-900 mb-2">
                              {item.units}
                            </div>
                            <div className="text-sm text-amber-700">unidades</div>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                          <div className="text-center mb-4">
                            <div className="text-3xl font-bold text-amber-950">
                              {item.price}
                            </div>
                          </div>
                          <div className="flex items-center justify-center gap-2 text-sm text-amber-700 mb-4">
                            <Check className="w-4 h-4" />
                            <span>{item.flavors} sabores</span>
                          </div>
                          <Badge variant="outline" className="w-full justify-center border-amber-300 text-amber-800 mb-4">
                            {item.weight}
                          </Badge>
                          <Button
                            className="w-full bg-amber-600 hover:bg-amber-700 text-white gap-2"
                            onClick={() => openFlavorSelector(item, 'brigadeiros')}
                          >
                            <CartIcon className="w-4 h-4" />
                            Selecionar Sabores
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="bombons" className="mt-0">
                <div className="mb-16">
                  <h3 className="text-2xl font-bold text-amber-900 mb-6 text-center">
                    Bombons de Chocolate Belga
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                    {pricingBombons.map((item, index) => (
                      <PricingCard key={index} item={item} index={index} type="bombons" />
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Info box */}
            <div className="mt-12 bg-amber-50 border-2 border-amber-200 rounded-2xl p-8 max-w-3xl mx-auto">
              <h4 className="font-bold text-xl text-amber-950 mb-4 text-center">
                Informações Importantes
              </h4>
              <div className="space-y-3 text-amber-800">
                <p className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>As encomendas devem ser feitas com no mínimo 3 dias de antecedência</span>
                </p>
                <p className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>A encomenda será efetivada após o pagamento de 50% do valor</span>
                </p>
                <p className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Os restantes 50% podem ser pagos no ato da entrega</span>
                </p>
              </div>
            </div>
          </div>

          {/* Carrinho Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-32">
              <ShoppingCartDisplay />
            </div>
          </div>
        </div>
      </div>

      {/* Modal de seleção de sabores */}
      <FlavorSelector 
        isOpen={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        onAddToCart={addToCart}
        boxInfo={currentBox}
        productType={currentType}
      />
    </section>
  );
};

export default Pricing;
